import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Box, Typography, IconButton, Button,
    TextField, CircularProgress, Stack, Tooltip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { parseApiDateTime } from '../../utils/parseApiDateTime';
import { clearRound } from '../../redux/slices/contactRound';
import { closeWhatsappConversationWithReason, getWhatsaAppThreads } from '../../redux/slices/whatsappV2';
import { callApi } from '../../utils/apiCall';
import { runOutcomeSteps, buildMessageSentSteps, buildSentResolvedSteps, buildBookedSteps, isLeadArchived, isLeadOptedOut, isOutcomeAllowed, getEmptyStateMessage, isLeadHydrating, closeRequiresConfirm, ARCHIVE_REASONS, FLAG_REASONS, getTaskTreatmentName, refreshAfterClose } from './resolvePanelOutcomes';
import AssignedToLead from '../../components/AssignedToLead';
import { compactChipStyle } from '../../styles/stepFormStyles';
import Iconify from '../../components/Iconify';
import { CRM_PRIMARY_BUTTON_SX } from '../../styles/crmButtons';
import { ACTION_META, DEFAULT_ACTION_META, COMPLETION_LABELS, DEFAULT_COMPLETION_LABEL } from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/actionMeta';
// Reuse the existing task-action code rather than rebuilding it:
//  • TaskAssigneeRow — the inline assignee-avatar + assign-picker used on the
//    guided checklist (renders avatars, opens AssignedToLead, PATCHes assignees).
//  • PauseTaskDialog + pauseTask — the same pause dialog + thunk the task
//    slideout uses (POST /tasks/{id}/pause/ with {resume_at}).
import TaskAssigneeRow from '../../sections/@dashboard/leadDetail/AttendedGuidedChecklist/TaskAssigneeRow';
import PauseTaskDialog from '../../components/dialogPopUp/PauseTaskDialog';
import ConfirmDialog from '../../components/dialogPopUp/ConfirmDialog';
import BookingPanel from './BookingPanel';

const RADIUS = '10px';
const WORKFLOW_ITEM_TYPES = new Set(['WHATSAPP_SENT', 'PHONE_CALLED', 'LOG_PHONE_CALL', 'LEAD_NEXT_ACTION', 'OPEN_FOLLOW_UP']);

const REMINDER_PRESETS = [
    { label: '2 hours', key: '2hours', getDate: () => { const d = new Date(); d.setHours(d.getHours() + 2); return d; } },
    { label: 'Tomorrow 9am', key: 'nextday', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
    { label: '1 week', key: 'week', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); return d; } },
    { label: '2 weeks', key: 'twoweek', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 14); d.setHours(9, 0, 0, 0); return d; } },
    { label: 'Custom', key: 'custom', getDate: null },
    { label: 'No reminder', key: 'none', getDate: null },
];

// Legacy reminder ids are numeric and route to /reminders/{id}/ — keep this
// numeric-only so a UUID is never misrouted to the reminders endpoint.
const isRealTaskId = (value) => value != null && /^\d+$/.test(String(value));

// Tasks-V2 task ids are UUIDs (e.g. 16da9e1d-df19-4549-...), though some legacy
// rows are still numeric. The /tasks/{id}/, /tasks/{id}/pause/ and assignees
// PATCH all accept either, so this gate accepts a non-empty numeric OR UUID id.
// Use ONLY for workflowTask / next / upcoming task gates — never for the
// /reminders/ paths (those stay on isRealTaskId).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isRealWorkflowTaskId = (value) => value != null && (/^\d+$/.test(String(value)) || UUID_RE.test(String(value)));

const isChecklistItemDone = (item) => Boolean(item?.completed_at || item?.is_completed || item?.completion_state === 'YES' || item?.state === 'YES');

const getChecklistItemId = (item) => item?.id || item?.checklist_item_id || item?.template_item_id;

const getWorkflowItemLabel = (item) => item?.title || item?.name || item?.label || 'First contact';

// Mirror the guided-checklist registry so the Resolve workflow card reads as the
// same system: icon + accent colour from ACTION_META, past-tense completion text
// from COMPLETION_LABELS. Falls back to the shared defaults for unknown types.
const getActionMeta = (item) => ACTION_META[item?.item_type] || DEFAULT_ACTION_META;
const getCompletionLabel = (item) => COMPLETION_LABELS[item?.item_type] || DEFAULT_COMPLETION_LABEL;

const getTaskLabel = (task) => task?.title || task?.name || task?.subject || 'Task';

const getTaskDueLabel = (task) => {
    const due = task?.due_at || task?.datetime;
    if (!due) return '';
    // parseApiDateTime reads the BE's DD-MM-YYYY[...]Z shape as UK-local wall-clock
    // (BST/GMT-safe) instead of the misleading literal 'Z' — see src/utils/parseApiDateTime.js.
    const date = parseApiDateTime(due);
    if (!date || !date.isValid()) return '';
    return date.calendar(null, {
        sameDay: '[Today] HH:mm',
        nextDay: '[Tomorrow] HH:mm',
        nextWeek: 'ddd HH:mm',
        lastDay: '[Yesterday] HH:mm',
        lastWeek: 'DD MMM HH:mm',
        sameElse: 'DD MMM YYYY',
    });
};

// Inbox destination labels — must match the conversation-list filter chips in
// src/pages/whatsapp/Contacts.jsx (MORE_MODES): value 'CLOSED' shows as
// "Resolved", value 'ARCHIVED' shows as "Archived". Naming the real tab in the
// success copy tells the rep exactly where the conversation went.
const RESOLVED_TAB_LABEL = 'Resolved';
const ARCHIVED_TAB_LABEL = 'Archived';

// Map a finishing outcome → the conversation RESULT + DESTINATION line shown on
// the completion card, so "what happened to the conversation, and where did it
// go" is explicit. Archive/spam land in the Archived tab; closing outcomes land
// in the Resolved tab. MESSAGE_SENT keeps the thread open, so its copy is the
// explicit keep-open variant.
const getConversationResultLabel = (outcome, reason, archiveReasons) => {
    if (outcome === 'MESSAGE_SENT') return 'Completed via WhatsApp — conversation remains open';
    if (outcome === 'APPOINTMENT_BOOKED') return `Booked — conversation moved to ${RESOLVED_TAB_LABEL}`;
    if (outcome === 'FOLLOW_UP') return `Follow-up set — conversation moved to ${RESOLVED_TAB_LABEL}`;
    if (outcome === 'SENT_RESOLVED') return `Conversation resolved — moved to ${RESOLVED_TAB_LABEL}`;
    if (outcome === 'OTHER') {
        if (archiveReasons.includes(reason)) return `Archived (${reason}) — moved to ${ARCHIVED_TAB_LABEL}`;
        if (reason === 'Spam') return `Marked as spam — moved to ${ARCHIVED_TAB_LABEL}`;
        return `Conversation resolved — moved to ${RESOLVED_TAB_LABEL}`;
    }
    return `Conversation resolved — moved to ${RESOLVED_TAB_LABEL}`;
};

const getResolvedAction = (outcome, closeReason, archiveReasons) => {
    if (outcome === 'MESSAGE_SENT') return 'message_sent';
    if (outcome === 'FOLLOW_UP') return 'follow_up';
    if (outcome === 'APPOINTMENT_BOOKED') return 'appointment_booked';
    if (outcome === 'OTHER' && archiveReasons.includes(closeReason)) return 'archive';
    if (outcome === 'OTHER' && closeReason === 'Spam') return 'archive';
    // "Resolved" / "General Chat" — ordinary close with no archive/spam. Previously
    // returned null, which silently skipped completing LEAD_NEXT_ACTION items
    // (they require a resolved_action). Mark them resolved so the checklist item
    // completes like every other close outcome.
    if (outcome === 'OTHER' && (closeReason === 'Resolved' || closeReason === 'General Chat')) return 'conversation_resolved';
    return null;
};

const findWorkflowItem = (task) => {
    const checklist = Array.isArray(task?.checklist_items) ? task.checklist_items : (task?.checklist || []);
    return checklist.find((entry) => (
        !isChecklistItemDone(entry)
        && (WORKFLOW_ITEM_TYPES.has(entry?.item_type) || /first contact|whatsapp|contact/i.test(getWorkflowItemLabel(entry)))
    )) || checklist.find((entry) => !isChecklistItemDone(entry)) || null;
};

// Resolve the checklist-item id to PAUSE for a given task: its workflow item
// (first incomplete contact item) or first incomplete item → its id. null when
// the task has no pausable checklist item (caller then disables Pause).
const getPauseItemId = (task) => {
    const item = findWorkflowItem(task);
    return item ? getChecklistItemId(item) : null;
};

// Mirror the app's small live-Task row (TaskRow in
// src/sections/tasks/LeadTaskList.jsx): the same Apple-grey card —
// complete-circle glyph, 13px title, due label with overdue/today colours,
// optional checklist counter — so the post-completion "what's next" items read
// as the existing task cards, not a bespoke ResolvePanel widget. Action slot
// (View / Reschedule) is passed in so the same row serves the next task (with
// controls) and the read-only "upcoming" tail.
const TASK_CARD = {
    text: '#1D1D1F',
    textSecondary: '#86868B',
    textTertiary: '#A1A1A6',
    success: '#34C759',
    danger: '#FF3B30',
};

// Normalise a Task's assignees into TaskAssigneeRow's initialAssignees shape:
// [{ id: <user_id>, name }]. The task list/detail endpoints return either
// `assignments` (preferred) or `assignees`; each entry may carry the user id as
// .user.id / .user_id / .practice_user?.user?.id / .id. TaskAssigneeRow
// cross-references by user id (pu.user?.id), so we resolve to that.
const getTaskAssigneeSeed = (task) => {
    let rows = [];
    if (Array.isArray(task?.assignments)) rows = task.assignments;
    else if (Array.isArray(task?.assignees)) rows = task.assignees;
    return rows
        .map((a) => {
            const userId = a?.user?.id ?? a?.user_id ?? a?.practice_user?.user?.id ?? a?.id;
            const name = a?.name || a?.full_name
                || (a?.user ? `${a.user.first_name || ''} ${a.user.last_name || ''}`.trim() : '')
                || a?.email || '';
            return userId != null ? { id: userId, name } : null;
        })
        .filter(Boolean);
};

const getNextTaskDueColor = (task) => {
    const due = task?.due_at || task?.datetime;
    if (!due) return TASK_CARD.textSecondary;
    const parsed = parseApiDateTime(due);
    if (!parsed || !parsed.isValid()) return TASK_CARD.textSecondary;
    if (parsed.isBefore(moment(), 'day')) return TASK_CARD.danger;
    if (parsed.isSame(moment(), 'day')) return TASK_CARD.success;
    return TASK_CARD.textSecondary;
};

const NextTaskCard = ({ task, action }) => {
    const progress = task?.checklist_progress;
    let checklistDone = null;
    let checklistTotal = null;
    if (typeof progress === 'string' && progress.includes('/')) {
        const [done, total] = progress.split('/').map((n) => parseInt(n, 10));
        if (Number.isFinite(done) && Number.isFinite(total)) { checklistDone = done; checklistTotal = total; }
    }
    const dueColor = getNextTaskDueColor(task);
    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', gap: '6px',
            py: '8px', px: '8px', borderRadius: '8px',
            border: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Iconify icon="eva:radio-button-off-outline" width={18} height={18} sx={{ color: TASK_CARD.textTertiary, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 400, lineHeight: 1.4, letterSpacing: '-0.01em', color: TASK_CARD.text }} noWrap>
                        {getTaskLabel(task)}
                    </Typography>
                    {getTaskDueLabel(task) && (
                        <Typography sx={{ fontSize: '11px', fontWeight: 500, lineHeight: 1.3, color: dueColor }} noWrap>
                            {getTaskDueLabel(task)}
                        </Typography>
                    )}
                </Box>
                {checklistTotal > 0 && (
                    <Typography sx={{
                        fontSize: '11px', fontWeight: 400, flexShrink: 0, whiteSpace: 'nowrap',
                        color: checklistDone === checklistTotal ? TASK_CARD.success : TASK_CARD.textSecondary,
                    }}>
                        {checklistDone}/{checklistTotal}
                    </Typography>
                )}
            </Box>
            {action && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: '26px' }}>
                    {action}
                </Box>
            )}
        </Box>
    );
};

NextTaskCard.propTypes = {
    task: PropTypes.object.isRequired,
    action: PropTypes.node,
};

// Action cluster for a next/upcoming task — surfaces the EXISTING task actions
// (Assign / Reschedule / Pause) inline so the rep can action upcoming work
// without leaving the conversation. Each action reuses the canonical code:
//  • Assign   → TaskAssigneeRow (renders avatars + opens AssignedToLead picker,
//               PATCHes /tasks/{id}/ {assignees:[{practice_user_id}]} itself)
//  • Reschedule → inline DateTimePicker → PATCH /tasks/{id}/ {due_at} (same
//               endpoint + shape as the panel's existing reschedule handler)
//  • Pause    → PauseTaskDialog → POST /tasks/{taskId}/checklist/{itemId}/pause/
//               {paused_until} (the real checklist-item pause action,
//               apps/tasks/comment_views.py ChecklistItemViewSet.pause). Pause
//               needs a pausable checklist item — pauseItemId is derived per task
//               by the caller; when absent, the Pause button is disabled.
export const NextTaskActions = ({ task, pauseItemId, practiceId, enqueueSnackbar, onRescheduled, onPaused }) => {
    const taskId = task?.id;
    // V2 task id (UUID or numeric) — gates Assign / Reschedule / Pause, all of
    // which hit /tasks/{id}/ endpoints that accept UUIDs.
    const realTask = isRealWorkflowTaskId(taskId);
    // Pause targets a checklist ITEM, so it also needs a resolved item id.
    const canPause = realTask && Boolean(pauseItemId);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [rescheduleDate, setRescheduleDate] = useState(null);
    const [rescheduleSaving, setRescheduleSaving] = useState(false);
    const [pauseOpen, setPauseOpen] = useState(false);
    const [pauseSaving, setPauseSaving] = useState(false);

    const openReschedule = () => {
        const existing = task?.due_at ? new Date(parseApiDateTime(task.due_at)) : null;
        const nearFuture = moment().add(1, 'day').hour(9).minute(0).second(0).toDate();
        setRescheduleDate(existing && existing.getTime() > Date.now() ? existing : nearFuture);
        setRescheduleOpen((prev) => !prev);
    };

    const handleRescheduleSave = async () => {
        if (!realTask || !practiceId || !rescheduleDate || rescheduleSaving) return;
        setRescheduleSaving(true);
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', {
                due_at: new Date(rescheduleDate).toISOString(),
            });
            if (response?.status === 200 || response?.ok) {
                const newDueAt = response?.data?.due_at || new Date(rescheduleDate).toISOString();
                setRescheduleOpen(false);
                // Format the JS Date the user picked for the toast — it always parses.
                // (newDueAt from the BE is DD-MM-YYYY, which bare moment() can't read →
                // "Invalid date"; rescheduleDate is the exact value the user set.)
                const pickedLabel = moment(rescheduleDate).format('DD MMM YYYY, HH:mm');
                setRescheduleDate(null);
                if (onRescheduled) onRescheduled(taskId, newDueAt);
                enqueueSnackbar(`Task moved to ${pickedLabel}`, {
                    variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' },
                });
            } else {
                throw new Error(response?.data?.detail || 'Failed to reschedule task');
            }
        } catch (err) {
            console.error('[ResolvePanel] Reschedule task error:', err);
            enqueueSnackbar('Could not reschedule the task — please try again', {
                variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        } finally {
            setRescheduleSaving(false);
        }
    };

    const handlePauseConfirm = async (resumeAt) => {
        if (!canPause || !practiceId || pauseSaving) return;
        setPauseSaving(true);
        try {
            // Real pause = the CHECKLIST-ITEM action (NOT a task-level /pause/ route,
            // which doesn't exist). POST /tasks/{taskId}/checklist/{itemId}/pause/
            // { paused_until: <ISO8601> }. resumeAt is PauseTaskDialog's ISO string.
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${pauseItemId}/pause/`,
                'post',
                { paused_until: resumeAt },
            );
            if (response?.status === 200 || response?.ok) {
                setPauseOpen(false);
                if (onPaused) onPaused(taskId, resumeAt);
                // parseApiDateTime handles both a genuine ISO string (real offset, as
                // PauseTaskDialog sends) and the BE's DD-MM-YYYY[...]Z UK-local shape,
                // so the toast never shows "Invalid date" or a BST/GMT-shifted time.
                const resumeMoment = parseApiDateTime(resumeAt);
                const resumeLabel = resumeMoment ? resumeMoment.format('DD MMM YYYY, HH:mm') : resumeAt;
                enqueueSnackbar(`Task paused — resumes ${resumeLabel}`, {
                    variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' },
                });
            } else {
                throw new Error(response?.data?.detail || 'Failed to pause task');
            }
        } catch (err) {
            console.error('[ResolvePanel] Pause task error:', err);
            enqueueSnackbar('Could not pause the task — please try again', {
                variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        } finally {
            setPauseSaving(false);
        }
    };

    const iconBtnSx = {
        minWidth: 0, px: 0.75, py: 0.25, fontSize: '11px', fontWeight: 600,
        textTransform: 'none', borderRadius: '7px',
        borderColor: 'rgba(145,158,171,0.24)', color: '#637381',
    };

    return (
        <Stack spacing={0.75} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {/* Assign — reuses the guided-checklist assignee row (avatars + picker) */}
                {realTask && (
                    <TaskAssigneeRow
                        practiceId={practiceId}
                        taskId={String(taskId)}
                        initialAssignees={getTaskAssigneeSeed(task)}
                        size={22}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                <Button size="small" variant="outlined" onClick={openReschedule} disabled={!realTask} sx={iconBtnSx}>
                    Reschedule
                </Button>
                {/* Disabled <Button> does not fire hover events, so wrap in a <span>
                    so MUI Tooltip can attach its listener to the span instead. */}
                <Tooltip title={!canPause ? 'No task step to pause.' : ''}>
                    <span>
                        <Button size="small" variant="outlined" onClick={() => setPauseOpen(true)} disabled={!canPause} sx={iconBtnSx}>
                            Pause
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {rescheduleOpen && (
                <Stack spacing={0.75}>
                    <DateTimePicker
                        value={rescheduleDate}
                        onChange={(val) => setRescheduleDate(val)}
                        disablePast
                        slotProps={{
                            textField: {
                                size: 'small', fullWidth: true, placeholder: 'Pick next due date',
                                sx: {
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: RADIUS, fontSize: '12px',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' },
                                    },
                                },
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
                        <Button size="small" onClick={() => setRescheduleOpen(false)} sx={{ textTransform: 'none', fontSize: '11px', color: '#637381' }}>
                            Cancel
                        </Button>
                        <Button size="small" variant="contained" disabled={!rescheduleDate || rescheduleSaving} onClick={handleRescheduleSave}
                            sx={{ textTransform: 'none', fontSize: '11px', bgcolor: '#00AB55', '&:hover': { bgcolor: '#007B55' } }}>
                            {rescheduleSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Stack>
            )}

            <PauseTaskDialog
                open={pauseOpen}
                onClose={() => setPauseOpen(false)}
                onConfirm={handlePauseConfirm}
                loading={pauseSaving}
            />
        </Stack>
    );
};

NextTaskActions.propTypes = {
    task: PropTypes.object.isRequired,
    pauseItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    enqueueSnackbar: PropTypes.func.isRequired,
    onRescheduled: PropTypes.func,
    onPaused: PropTypes.func,
};

export default function ResolvePanel({ open, onClose, onConfirmClose, contactDetails, practiceId, taskId, onShareMessage, onOpenWorkflow, _testWorkflowCompletion }) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const whatsAppThread = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.thread);

    const [selectedOutcome, setSelectedOutcome] = useState(null);
    const [notes, setNotes] = useState('');
    const [closeReason, setCloseReason] = useState('');
    const [calledConfirmed, setCalledConfirmed] = useState(null);
    const [selectedCallOptions, setSelectedCallOptions] = useState([]);
    const [callNote, setCallNote] = useState('');
    const [reminderPreset, setReminderPreset] = useState(null);
    const [customDate, setCustomDate] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveResult, setSaveResult] = useState(null); // null | 'success' | 'error'
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [assignToState, setAssignToState] = useState([]);
    const [workflowTask, setWorkflowTask] = useState(null);
    const [workflowItem, setWorkflowItem] = useState(null);
    const [workflowTasks, setWorkflowTasks] = useState([]);
    const [workflowLoading, setWorkflowLoading] = useState(false);
    const [workflowCompletion, setWorkflowCompletion] = useState(_testWorkflowCompletion ?? null);
    // Bumped to force the workflow fetch to re-run after an action that mutates
    // the CURRENT task out from under the panel (e.g. pausing it) — the fetch
    // re-picks the new current item, or lands on the empty/none state.
    const [workflowRefreshKey, setWorkflowRefreshKey] = useState(0);

    const whatsAppLeadDetails = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.leadDetails);
    const leadDetails = whatsAppLeadDetails || contactDetails?.leadDetails;
    const leadId = leadDetails?.id;
    // True while Redux hasn't yet populated leadDetails for the current thread.
    // Redux always sets leadDetails to either a real object (id present) or {}
    // (fetch complete, no linked lead) — only `undefined` means "not yet loaded".
    // We use the raw whatsAppLeadDetails rather than the merged leadDetails so
    // that a contactDetails fallback doesn't mask a still-hydrating Redux state.
    const leadHydrating = isLeadHydrating(whatsAppLeadDetails);

    // ── Lead-state guards (spec #8) ──
    // isArchived: is_archived OR archived_at set OR is_spam — block ALL actions, read-only.
    // isOptedOut: is_open_for_communication === false (and NOT archived) — block contact
    //   outcomes, keep administrative close (OTHER) available.
    const isArchived = isLeadArchived(leadDetails);
    const isOptedOut = isLeadOptedOut(leadDetails);
    // The conversation identity. whatsAppThread comes straight from the
    // whatsappV2 chat-metadata slice (set atomically by saveWhatsappChatMetaData
    // when the rep opens a thread), so it flips the instant the conversation
    // switches — more reliable than leadId, which is derived from nested
    // leadDetails and can lag a frame behind a thread switch.
    const threadId = whatsAppThread?.id;
    const treatmentId = leadDetails?.lead_treatments?.[0]?.id
        || leadDetails?.lead_treatment?.id
        || contactDetails?.leadDetails?.lead_treatments?.[0]?.id;

    // Reset ALL transient panel state to the fresh initial view. Shared by the
    // close path and the conversation-switch path below so both land on
    // "current task + What happened? picker" for the active conversation.
    const resetPanelState = useCallback(() => {
        setSelectedOutcome(null);
        setNotes('');
        setCloseReason('');
        setCalledConfirmed(null);
        setReminderPreset(null);
        setCustomDate(null);
        setIsSaving(false);
        setSaveResult(null);
        setAssignToState([]);
        setSelectedCallOptions([]);
        setCallNote('');
        setWorkflowTask(null);
        setWorkflowItem(null);
        setWorkflowTasks([]);
        setWorkflowLoading(false);
        setWorkflowCompletion(null);
    }, []);

    useEffect(() => {
        if (!open) {
            dispatch(clearRound());
            resetPanelState();
        }
    }, [open, dispatch, resetPanelState]);

    // FIX: reset on CONVERSATION switch. The close effect above only fires when
    // the panel closes; when the rep switches to another conversation while the
    // panel stays open, the thread/lead changes but the previous conversation's
    // state (completion card / selectedOutcome / saveResult) used to persist.
    // Keyed on the thread identity (+ lead/treatment as a backstop) so any switch
    // clears back to the new conversation's initial state. Skipped on close
    // (handled above) and on the very first open of a thread (nothing to clear).
    const prevThreadKeyRef = useRef(null);
    useEffect(() => {
        if (!open) { prevThreadKeyRef.current = null; return; }
        const key = `${threadId ?? ''}:${leadId ?? ''}:${treatmentId ?? ''}`;
        if (prevThreadKeyRef.current !== null && prevThreadKeyRef.current !== key) {
            resetPanelState();
        }
        prevThreadKeyRef.current = key;
    }, [open, threadId, leadId, treatmentId, resetPanelState]);

    useEffect(() => {
        if (!open || !practiceId) {
            setWorkflowTask(null);
            setWorkflowItem(null);
            setWorkflowTasks([]);
            return undefined;
        }
        // While the thread's leadDetails is still arriving from Redux, hold the
        // loading state rather than clearing to the empty/no-task view.  Once
        // leadHydrating resolves (leadDetails becomes {} or a real object) this
        // effect re-fires (leadHydrating is derived from whatsAppLeadDetails which
        // is in the dep array via leadId) and the normal path runs.
        if (leadHydrating) {
            setWorkflowLoading(true);
            return undefined;
        }
        if (!isRealTaskId(taskId) && !leadId) {
            setWorkflowTask(null);
            setWorkflowItem(null);
            setWorkflowTasks([]);
            return undefined;
        }

        let active = true;
        setWorkflowLoading(true);

        const loadTaskDetail = (linkedTaskId) => callApi(`/api/v1/practices/${practiceId}/tasks/${linkedTaskId}/`);
        const resolveTaskRequest = async () => {
            const detailResponses = [];
            if (isRealTaskId(taskId)) {
                detailResponses.push(await loadTaskDetail(taskId).catch(() => null));
            }

            if (leadId) {
                const listResponse = await callApi(`/api/v1/practices/${practiceId}/tasks/?lead=${leadId}&status__in=pending,in_progress&ordering=due_at&limit=5`).catch(() => null);
                const rows = listResponse?.data?.results || listResponse?.data || [];
                const candidates = Array.isArray(rows) ? rows : [];
                const seen = new Set(detailResponses.map((response) => response?.data?.id).filter(Boolean).map(String));
                const missingIds = candidates
                    .map((candidate) => candidate?.id)
                    .filter(Boolean)
                    .filter((candidateId) => !seen.has(String(candidateId)));
                const listDetails = await Promise.all(missingIds.map((candidateId) => loadTaskDetail(candidateId).catch(() => null)));
                detailResponses.push(...listDetails);
            }

            const taskDetails = detailResponses
                .filter((response) => response?.status === 200 && response?.data)
                .map((response) => response.data);
            const selectedTask = taskDetails.find((task) => findWorkflowItem(task)) || taskDetails[0] || null;
            return { selectedTask, taskDetails };
        };

        resolveTaskRequest()
            .then(({ selectedTask, taskDetails }) => {
                if (!active) return;
                if (!selectedTask) {
                    setWorkflowTask(null);
                    setWorkflowItem(null);
                    setWorkflowTasks([]);
                    return;
                }
                const item = findWorkflowItem(selectedTask);
                setWorkflowTask(selectedTask);
                setWorkflowItem(item || null);
                setWorkflowTasks(taskDetails);
            })
            .catch(() => {
                if (!active) return;
                setWorkflowTask(null);
                setWorkflowItem(null);
                setWorkflowTasks([]);
            })
            .finally(() => {
                if (active) setWorkflowLoading(false);
            });

        return () => { active = false; };
    // threadId included so the workflow reloads the instant the conversation
    // switches, even if the derived leadId lags a frame behind the thread.
    // leadHydrating included so the effect re-fires the moment Redux finishes
    // populating leadDetails (undefined → {} or real object) and the real fetch
    // can start — prevents the panel staying permanently stuck on loading.
    // workflowRefreshKey forces a re-pick after the current task is paused.
    }, [open, practiceId, taskId, leadId, leadHydrating, threadId, workflowRefreshKey]);

    // Most outcomes show the completion card after the checklist item is
    // completed. The optional silent mode is retained for future callers that
    // need a pure backend completion without changing the panel UI.
    const completeWorkflowItem = async (outcome, reason, { silent = false } = {}) => {
        if (!workflowItem || !workflowTask || !practiceId) return { success: true, completed: false };
        const itemId = getChecklistItemId(workflowItem);
        const linkedTaskId = workflowTask?.id || taskId;
        if (!itemId || !linkedTaskId) return { success: true, completed: false };

        const body = { completion_state: 'YES' };
        const resolvedAction = getResolvedAction(outcome, reason, ARCHIVE_REASONS);
        if (workflowItem?.item_type === 'LEAD_NEXT_ACTION') {
            if (!resolvedAction) return { success: true, completed: false };
            body.response_payload = { resolved_action: resolvedAction };
        }

        const response = await callApi(
            `/api/v1/practices/${practiceId}/tasks/${linkedTaskId}/checklist/${itemId}/complete/`,
            'post',
            body
        );
        const success = response?.status === 200 || response?.ok;
        if (!success) return { success: false, completed: false };

        if (silent) {
            // Item is done on the BE, but no completion card — keep the open-thread feel.
            setWorkflowItem(null);
            return { success: true, completed: false };
        }

        const nextTask = workflowTasks
            .filter((task) => String(task?.id) !== String(linkedTaskId))
            .find(Boolean) || null;
        setWorkflowCompletion({
            completedTask: workflowTask,
            completedItem: workflowItem,
            nextTask,
            completedAt: new Date(),
            outcome,
            reason,
        });
        setWorkflowItem(null);
        return { success: true, completed: true };
    };

    // NextTaskActions owns the reschedule PATCH + its own snackbar; these two
    // callbacks just reconcile the panel's local view of the tasks afterwards.
    // applyTaskReschedule patches the due date onto the matching nextTask /
    // upcoming row in place.
    const applyTaskReschedule = useCallback((rescheduledTaskId, newDueAt) => {
        setWorkflowCompletion((prev) => (prev && String(prev.nextTask?.id) === String(rescheduledTaskId)
            ? { ...prev, nextTask: { ...prev.nextTask, due_at: newDueAt } }
            : prev));
        setWorkflowTasks((prev) => prev.map((task) => (
            String(task?.id) === String(rescheduledTaskId) ? { ...task, due_at: newDueAt } : task
        )));
        // Also reflect on the current-task card when it's the one rescheduled.
        setWorkflowTask((prev) => (prev && String(prev.id) === String(rescheduledTaskId)
            ? { ...prev, due_at: newDueAt }
            : prev));
    }, []);

    // dropUpcomingTask removes a just-paused task from the next/upcoming view —
    // a paused task is no longer "next up". If it was the surfaced nextTask, the
    // next remaining task (from the post-removal list) takes its place.
    const dropUpcomingTask = useCallback((pausedTaskId) => {
        let remaining = [];
        setWorkflowTasks((prev) => {
            remaining = prev.filter((task) => String(task?.id) !== String(pausedTaskId));
            return remaining;
        });
        setWorkflowCompletion((prev) => {
            if (!prev || String(prev.nextTask?.id) !== String(pausedTaskId)) return prev;
            const replacement = remaining.find(
                (task) => String(task?.id) !== String(prev.completedTask?.id)
            ) || null;
            return { ...prev, nextTask: replacement };
        });
    }, []);

    // Pausing the CURRENT task (the one the panel is centred on) — the card must
    // not be left stale showing a now-paused task. Clear the current task/item
    // and bump the refresh key so the workflow fetch re-runs and re-picks the
    // next open task; if none remain, the fetch resolves to no current-task card
    // and the picker stands alone.
    const handleCurrentTaskPaused = useCallback(() => {
        setWorkflowItem(null);
        setWorkflowTask(null);
        setWorkflowRefreshKey((k) => k + 1);
    }, []);

    const handleMessageSent = async () => {
        if (isSaving) return;
        setIsSaving(true);

        // MESSAGE_SENT: note → checklist complete. No reminder-complete,
        // no thread-close — the conversation stays open, and the completion
        // card shows the next task / checklist handoff.
        const steps = buildMessageSentSteps({ leadId, practiceId, notes, treatmentId, callApi, completeWorkflowItem });

        const outcome = await runOutcomeSteps(steps);
        if (!outcome.success) {
            console.error('[ResolvePanel] Message sent error at step:', outcome.failedStep, outcome.error);
            const succeeded = outcome.successfulSteps.length
                ? `Completed: ${outcome.successfulSteps.join(', ')}. `
                : '';
            enqueueSnackbar(
                `${succeeded}Failed at "${outcome.failedStep}" — conversation left open so you can retry`,
                { variant: 'error', autoHideDuration: 5000 },
            );
            setSaveResult('error');
            setIsSaving(false);
            return;
        }

        setSaveResult('success');
        setIsSaving(false);
    };

    // ── Sent — resolved handler ──
    // Same "the message was sent" completion as handleMessageSent, but the rep is
    // saying the conversation is finished too: complete the checklist item with the
    // conversation_resolved action AND close the thread with reason "Resolved". Lands
    // on the completion card (Done button) like the close/follow-up paths.
    //
    // ORDER: note → checklist complete → thread close LAST (no reminder step).
    const handleSentResolved = async () => {
        if (isSaving) return;
        setIsSaving(true);

        const steps = buildSentResolvedSteps({
            leadId, practiceId, threadId, notes, treatmentId,
            callApi, completeWorkflowItem, dispatch, closeWhatsappConversationWithReason,
        });

        const outcome = await runOutcomeSteps(steps);
        if (!outcome.success) {
            console.error('[ResolvePanel] Sent — resolved error at step:', outcome.failedStep, outcome.error);
            const succeeded = outcome.successfulSteps.length
                ? `Completed: ${outcome.successfulSteps.join(', ')}. `
                : '';
            enqueueSnackbar(
                `${succeeded}Failed at "${outcome.failedStep}" — conversation left open so you can retry`,
                { variant: 'error', autoHideDuration: 5000 },
            );
            setSaveResult('error');
            setIsSaving(false);
            return;
        }

        setSaveResult('success');
        // Refresh the conversation list so the closed thread is removed from
        // the open-inbox view. refreshAfterClose is a no-op when there is no
        // 'close' step (message-sent path) or when the outcome failed.
        refreshAfterClose({ outcome, steps, dispatch, getWhatsaAppThreads });
        // Pick the completeWorkflowItem result from the ordered results array.
        const completeIdx = steps.findIndex((s) => s.name === 'complete');
        const workflowResult = completeIdx >= 0 ? outcome.results?.[completeIdx] : null;
        if (!workflowResult?.completed) {
            setTimeout(() => { onConfirmClose('Resolved'); }, 1200);
        }
        setIsSaving(false);
    };

    // ── Appointment Booked handler ──
    //
    // Called by BookingPanel via onBooked after the appointment has been created.
    // ORDER: checklist complete → thread close LAST. No note step — the appointment
    // record is the audit trail. If checklist fails the thread stays open so the
    // rep can retry; the same partial-failure snackbar pattern as other handlers.
    const handleBooked = async () => {
        if (isSaving) return;
        setIsSaving(true);

        const steps = buildBookedSteps({
            leadId, practiceId, threadId,
            completeWorkflowItem, dispatch, closeWhatsappConversationWithReason,
        });

        const outcome = await runOutcomeSteps(steps);
        if (!outcome.success) {
            console.error('[ResolvePanel] Booked error at step:', outcome.failedStep, outcome.error);
            const succeeded = outcome.successfulSteps.length
                ? `Completed: ${outcome.successfulSteps.join(', ')}. `
                : '';
            enqueueSnackbar(
                `${succeeded}Failed at "${outcome.failedStep}" — conversation left open so you can retry`,
                { variant: 'error', autoHideDuration: 5000 },
            );
            setSaveResult('error');
            setIsSaving(false);
            return;
        }

        setSaveResult('success');
        refreshAfterClose({ outcome, steps, dispatch, getWhatsaAppThreads });
        const completeIdx = steps.findIndex((s) => s.name === 'complete');
        const workflowResult = completeIdx >= 0 ? outcome.results?.[completeIdx] : null;
        // completed:false → no completion card is rendered (no workflow item existed
        // or the item type skipped), so dismiss the panel directly here.
        // completed:true → the completion card has mounted and owns its own dismissal
        // via its Done/View-next-task button; do not auto-dismiss over it.
        if (!workflowResult?.completed) {
            setTimeout(() => { onConfirmClose('Booked'); }, 1200);
        }
        setIsSaving(false);
    };

    // ── Follow Up handler ──
    //
    // ORDER: note → new-reminder → To-Do reminder complete → checklist complete → thread close LAST.
    // Rationale: closing the thread before the checklist is marked done means a
    // 500 on the checklist leaves the conversation silently archived in the CRM
    // but the task looking unfinished. Keeping close last means a failed checklist
    // leaves the thread open and the rep sees the error and can retry.
    const handleFollowUp = async () => {
        if (!reminderPreset || (reminderPreset === 'custom' && !customDate) || isSaving) return;
        setIsSaving(true);

        const backendOutcome = calledConfirmed ? 'SPOKE_NOT_BOOKED' : 'UNABLE_TO_CONTACT';

        // Pre-compute the reminder date outside the step so the value is stable.
        const preset = REMINDER_PRESETS.find(p => p.key === reminderPreset);
        const reminderDate = reminderPreset === 'custom' ? new Date(customDate) : preset?.getDate?.();
        const presetLabel = reminderPreset === 'custom' ? moment(customDate).format('DD MMM, HH:mm') : preset?.label;

        const steps = [
            // 1. Note
            ...(leadId && practiceId ? [{
                name: 'note',
                run: () => {
                    const callInfo = calledConfirmed && callNote ? `Call: ${callNote}` : '';
                    return callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                        note: backendOutcome === 'SPOKE_NOT_BOOKED' ? 'FOLLOW_UP' : 'UNABLE_TO_CONTACT',
                        additional_notes: [callInfo, notes].filter(Boolean).join(' | ') || 'WhatsApp follow up',
                        contacted_via: calledConfirmed ? 'PHONE' : 'WHATSAPP',
                        lead: leadId,
                        ...(treatmentId ? { treatment: treatmentId } : {}),
                    });
                },
            }] : []),
            // 2. New reminder (skip if "No reminder" or no date/lead)
            ...(reminderPreset !== 'none' && reminderDate && leadId && practiceId ? [{
                name: 'reminder',
                run: () => callApi(`/api/v1/practices/${practiceId}/reminders/`, 'post', {
                    method: calledConfirmed ? 'CALL' : 'WHATSAPP',
                    datetime: `${moment(reminderDate).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
                    note: notes || `Follow up - ${presetLabel}`,
                    is_completed: false,
                    group: 'FOLLOW_UP',
                    lead: leadId,
                    lead_treatment: treatmentId || undefined,
                    assignees: assignToState?.map(el => el?.id).filter(Boolean) || [],
                }),
            }] : []),
            // 3. Complete To-Do task if this panel was opened from a real reminder.
            // WhatsApp virtual cards use ids like `wa-123` and must not hit /reminders/{id}/.
            ...(isRealTaskId(taskId) && practiceId ? [{
                name: 'todo',
                run: () => callApi(`/api/v1/practices/${practiceId}/reminders/${taskId}/`, 'patch', { is_completed: true }),
            }] : []),
            // 4. Checklist complete — must run before close.
            {
                name: 'complete',
                run: () => completeWorkflowItem('FOLLOW_UP', 'Follow Up'),
            },
            // 5. Close WhatsApp thread LAST — only if all prior steps succeeded.
            ...(threadId && leadId ? [{
                name: 'close',
                run: () => dispatch(closeWhatsappConversationWithReason(threadId, 'Follow Up', leadId)),
            }] : []),
        ];

        const outcome = await runOutcomeSteps(steps);
        if (!outcome.success) {
            console.error('[ResolvePanel] Follow up error at step:', outcome.failedStep, outcome.error);
            const succeeded = outcome.successfulSteps.length
                ? `Completed: ${outcome.successfulSteps.join(', ')}. `
                : '';
            enqueueSnackbar(
                `${succeeded}Failed at "${outcome.failedStep}" — conversation left open so you can retry`,
                { variant: 'error', autoHideDuration: 5000 },
            );
            setSaveResult('error');
            setIsSaving(false);
            return;
        }

        setSaveResult('success');
        refreshAfterClose({ outcome, steps, dispatch, getWhatsaAppThreads });
        const completeIdx = steps.findIndex((s) => s.name === 'complete');
        const workflowResult = completeIdx >= 0 ? outcome.results?.[completeIdx] : null;
        if (!workflowResult?.completed) {
            setTimeout(() => { onConfirmClose('Follow Up'); }, 1200);
        }
        setIsSaving(false);
    };

    // ── Close handler — different actions based on close reason ──
    // ARCHIVE_REASONS and FLAG_REASONS are imported from resolvePanelOutcomes.js.
    // Map frontend labels → valid NOTE_CHOICES (apps/commons/choices.py). EVERY
    // close reason that reaches the note POST must be listed here; anything not
    // mapped falls back to 'OTHER' (a valid enum) rather than a brittle
    // toUpperCase() transform that produced invalid values like GENERAL_CHAT /
    // RESOLVED → LeadNote 400. The human-readable reason is still saved to the
    // WhatsApp thread metadata separately, so 'OTHER' on the note is fine.
    const REASON_TO_NOTE_CHOICE = {
        'Not Interested': 'NOT_INTERESTED',
        'Too Expensive': 'TOO_EXPENSIVE',
        'Gone Elsewhere': 'ANOTHER_PRACTICE',
        'Duplicate': 'DUPLICATE_ENTRY',
        'Spam': 'SPAM',
    };

    // ── runClose — executes the full close sequence (note → archive/flag → complete → close).
    // Called directly for ordinary reasons (Resolved, General Chat) and by the
    // confirm dialog's onConfirm for archive/spam reasons.
    const runClose = async () => {
        if (!closeReason || isSaving) return;
        setIsSaving(true);

        const contactedVia = calledConfirmed ? 'PHONE' : 'WHATSAPP';
        const treatmentField = treatmentId ? { treatment: treatmentId } : {};
        const noteReason = REASON_TO_NOTE_CHOICE[closeReason] || 'OTHER';
        const isArchive = ARCHIVE_REASONS.includes(closeReason);
        const isSpam = FLAG_REASONS.includes(closeReason);

        // Build the ordered step list for this close reason.
        //
        // Archive ORDER: note → archive → checklist complete (gated) → close LAST.
        // Spam ORDER:    note → flag   → checklist complete (gated) → close LAST.
        // Ordinary:      note → todo   → checklist complete         → close LAST.
        //
        // Rationale: marking the workflow complete before the key business action
        // (archive/flag) succeeds would make the CRM look finished when it failed.
        const steps = [
            // 1. Note (all paths)
            ...(leadId && practiceId ? [{
                name: 'note',
                run: () => {
                    if (isArchive) {
                        return callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                            note: noteReason, additional_notes: notes || `Archived: ${closeReason}`, lead: leadId,
                            contacted_via: contactedVia, ...treatmentField,
                        });
                    }
                    if (isSpam) {
                        return callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                            note: 'SPAM', additional_notes: notes || 'Marked as spam from WhatsApp', lead: leadId,
                            contacted_via: contactedVia, ...treatmentField,
                        });
                    }
                    return callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                        note: 'OTHER', additional_notes: `WhatsApp closed: ${closeReason}${notes ? ` — ${notes}` : ''}`, lead: leadId,
                        contacted_via: contactedVia, ...treatmentField,
                    });
                },
            }] : []),
            // 2a. Archive (archive path only) — gates complete.
            ...(leadId && practiceId && isArchive ? [{
                name: 'archive',
                run: () => callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/archive/`, 'post', {
                    reason: noteReason, note: notes || `WhatsApp closed: ${closeReason}`,
                }),
            }] : []),
            // 2b. Flag as spam (spam path only) — gates complete.
            ...(leadId && practiceId && isSpam ? [{
                name: 'flag',
                run: () => callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/mark_as_spam/`, 'post'),
            }] : []),
            // 3. Complete To-Do task if opened from a real reminder.
            ...(isRealTaskId(taskId) && practiceId ? [{
                name: 'todo',
                run: () => callApi(`/api/v1/practices/${practiceId}/reminders/${taskId}/`, 'patch', { is_completed: true }),
            }] : []),
            // 4. Checklist complete — must run before close.
            // Opted-out leads: skip the checklist item — the lead has closed
            // communications so we do NOT mark an outreach task done; we still
            // write the note + close the thread for administrative hygiene.
            ...(!isOptedOut ? [{
                name: 'complete',
                run: () => completeWorkflowItem('OTHER', closeReason),
            }] : []),
            // 5. Thread close LAST — only runs if all prior steps succeeded.
            ...(threadId && leadId ? [{
                name: 'close',
                run: () => dispatch(closeWhatsappConversationWithReason(threadId, closeReason, leadId)),
            }] : []),
        ];

        const outcome = await runOutcomeSteps(steps);
        if (!outcome.success) {
            console.error('[ResolvePanel] Close error at step:', outcome.failedStep, outcome.error);
            const succeeded = outcome.successfulSteps.length
                ? `Completed: ${outcome.successfulSteps.join(', ')}. `
                : '';
            enqueueSnackbar(
                `${succeeded}Failed at "${outcome.failedStep}" — conversation left open so you can retry`,
                { variant: 'error', autoHideDuration: 5000 },
            );
            setSaveResult('error');
            setIsSaving(false);
            return;
        }

        setSaveResult('success');
        refreshAfterClose({ outcome, steps, dispatch, getWhatsaAppThreads });
        const completeIdx = steps.findIndex((s) => s.name === 'complete');
        const workflowResult = completeIdx >= 0 ? outcome.results?.[completeIdx] : null;
        if (!workflowResult?.completed) {
            setTimeout(() => { onConfirmClose(closeReason); }, 1200);
        }
        setIsSaving(false);
    };

    // ── handleClose — confirm gate for archive/spam; direct run for all others.
    // Archive and Spam change lead state (irreversible), so they get a
    // confirmation dialog before runClose fires. Resolved / General Chat
    // remain one-click — no dialog shown.
    const handleClose = () => {
        if (!closeReason || isSaving) return;
        if (closeRequiresConfirm(closeReason)) {
            setConfirmOpen(true);
        } else {
            runClose();
        }
    };

    const hasWorkflowTask = Boolean(workflowTask);
    const hasWorkflowItem = Boolean(workflowItem && workflowTask);
    // Empty-state descriptor — null when the panel should render normally.
    // Derived from leadId + leadHydrating + workflowLoading + workflowItem (spec #3).
    // leadHydrating suppresses the 'no-lead' state while Redux is still loading.
    const emptyState = getEmptyStateMessage({ leadId, leadHydrating, workflowLoading, workflowItem });
    const workflowLabel = hasWorkflowItem ? getWorkflowItemLabel(workflowItem) : '';
    // Treatment name for the current-task title suffix (spec #5). Resolves via
    // task.lead_treatment.treatment_name (nested object) or, when the field is an
    // id, by matching against leadDetails.lead_treatments[]. Empty string when
    // unresolvable — suppresses the separator so no dangling "· " appears.
    const taskTreatmentName = getTaskTreatmentName(workflowTask, leadDetails);
    // Action-type icon/colour for the current-task card — resolves from the linked
    // checklist item when present, otherwise the task itself, mirroring the
    // guided-checklist registry so the card reads as the same system.
    const workflowItemMeta = getActionMeta(workflowItem || workflowTask);
    const workflowSuffix = hasWorkflowItem ? ' & complete task' : '';
    const upcomingWorkflowTasks = workflowTasks
        .filter((task) => String(task?.id) !== String(workflowTask?.id))
        .slice(0, 3);

    // ── Button config based on state ──
    const getFooterButton = () => {
        // APPOINTMENT_BOOKED replaces the whole panel with an embedded BookingPanel
        // (early-return below, before the main panel renders) — it has its own
        // internal submit flow and never needs a footer button from here.
        if (!selectedOutcome || selectedOutcome === 'APPOINTMENT_BOOKED') return null;

        // On success there is exactly ONE primary CTA per state: the completion card's
        // "Done" (resolve/close/book/follow-up) or, for the keep-open MESSAGE_SENT
        // path, a light banner + auto-return. The footer no longer renders a competing
        // success-echo button.
        if (saveResult === 'success') return null;

        if (selectedOutcome === 'MESSAGE_SENT') {
            return {
                label: isSaving ? 'Saving...' : `Mark sent${workflowSuffix}`,
                icon: isSaving ? null : 'eva:checkmark-circle-2-fill',
                disabled: isSaving,
                onClick: handleMessageSent,
                color: '#00AB55',
            };
        }

        if (selectedOutcome === 'SENT_RESOLVED') {
            return {
                label: isSaving ? 'Saving...' : `Mark sent & resolve${workflowSuffix}`,
                icon: isSaving ? null : 'eva:checkmark-circle-2-fill',
                disabled: isSaving,
                onClick: handleSentResolved,
                color: '#00AB55',
            };
        }

        if (selectedOutcome === 'FOLLOW_UP') {
            return {
                label: isSaving ? 'Saving...' : `Set follow-up${workflowSuffix}`,
                icon: isSaving ? null : 'eva:checkmark-circle-2-fill',
                disabled: !reminderPreset || (reminderPreset === 'custom' && !customDate) || calledConfirmed === null || isSaving,
                onClick: handleFollowUp,
                color: '#00AB55',
            };
        }

        if (selectedOutcome === 'OTHER') {
            /* eslint-disable no-nested-ternary */
            const closeLabel = isSaving ? 'Closing...' : ARCHIVE_REASONS.includes(closeReason) ? `Archive${workflowSuffix}` : FLAG_REASONS.includes(closeReason) ? `Flag${workflowSuffix}` : closeReason ? `Close${workflowSuffix}` : 'Close & Resolve';
            return {
                label: closeLabel,
                icon: isSaving ? null : (ARCHIVE_REASONS.includes(closeReason) ? 'eva:archive-fill' : FLAG_REASONS.includes(closeReason) ? 'eva:flag-fill' : 'mdi:close-circle-outline'),
                disabled: !closeReason || calledConfirmed === null || isSaving,
                onClick: handleClose,
                color: ARCHIVE_REASONS.includes(closeReason) ? '#FFAB00' : FLAG_REASONS.includes(closeReason) ? '#FF5630' : '#637381',
            };
            /* eslint-enable no-nested-ternary */
        }

        return null;
    };

    const footerButton = getFooterButton();

    const outcomeLabels = {
        MESSAGE_SENT: 'Sent — awaiting reply',
        SENT_RESOLVED: 'Sent — resolved',
        // APPOINTMENT_BOOKED renders the embedded BookingPanel (early-return below)
        // so this label is never shown in the header, but included for completeness.
        APPOINTMENT_BOOKED: 'Book',
        FOLLOW_UP: 'Follow-up needed',
        OTHER: 'Close',
    };

    // ── Appointment Booked → embedded BookingPanel ──
    if (selectedOutcome === 'APPOINTMENT_BOOKED') {
        return (
            <Box sx={{
                width: open ? 360 : 0, transition: 'width 300ms ease', overflow: 'hidden',
                flexShrink: 0, borderLeft: open ? '1px solid rgba(145,158,171,0.2)' : 'none', height: '100%',
            }}>
                <Box sx={{ width: 360, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.12)', flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <IconButton size="small" onClick={() => setSelectedOutcome(null)} sx={{ width: 24, height: 24, mr: 0.25 }}>
                                <Icon icon="eva:arrow-back-fill" width={16} />
                            </IconButton>
                            <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#212B36' }}>Book</Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" sx={{ width: 28, height: 28 }}>
                            <Icon icon="eva:close-fill" width={18} />
                        </IconButton>
                    </Box>
                    <BookingPanel
                        open
                        onClose={() => { setSelectedOutcome(null); onClose(); }}
                        contactDetails={contactDetails}
                        practiceId={practiceId}
                        taskId={taskId}
                        onShareMessage={onShareMessage}
                        embedded
                        onBooked={handleBooked}
                    />
                </Box>
            </Box>
        );
    }

    // ── Main panel ──
    return (
        <Box sx={{
            width: open ? 360 : 0, transition: 'width 300ms ease', overflow: 'hidden',
            flexShrink: 0, borderLeft: open ? '1px solid rgba(145,158,171,0.2)' : 'none', height: '100%',
        }}>
            <Box sx={{ width: 360, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.12)', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {selectedOutcome && (
                            <IconButton size="small" onClick={() => { setSelectedOutcome(null); setSaveResult(null); }} sx={{ width: 24, height: 24, mr: 0.25 }}>
                                <Icon icon="eva:arrow-back-fill" width={16} />
                            </IconButton>
                        )}
                        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#212B36' }}>
                            {selectedOutcome ? outcomeLabels[selectedOutcome] || 'Resolve' : 'Resolve'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ width: 28, height: 28 }}>
                        <Icon icon="eva:close-fill" width={18} />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2 }}>
                    {workflowCompletion && (
                        <Box sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: '#fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
                        }}>
                            <Stack spacing={1.5}>
                                {/* Eyebrow — mirrors UpNextCard's dot + uppercase label */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#34c759', flexShrink: 0 }} />
                                    <Typography component="span" sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#34c759' }}>
                                        Completed
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <Box sx={{
                                        width: 44, height: 44, borderRadius: '12px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        bgcolor: 'rgba(52,199,89,0.12)', border: '1.5px solid rgba(52,199,89,0.28)', flexShrink: 0,
                                    }}>
                                        <Iconify icon="mdi:check-circle" width={22} height={22} sx={{ color: '#34c759' }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {/* Title names the conversation RESULT + DESTINATION tab
                                            (Resolved / Archived) so the rep knows where the thread
                                            went; the checklist-completion label drops to the subline
                                            as context. */}
                                        <Typography sx={{ fontSize: '15px', color: '#1d1d1f', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
                                            {getConversationResultLabel(workflowCompletion.outcome, workflowCompletion.reason, ARCHIVE_REASONS)}
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#6e6e73', lineHeight: 1.3, mt: 0.25 }} noWrap>
                                            {getCompletionLabel(workflowCompletion.completedItem)} · {getWorkflowItemLabel(workflowCompletion.completedItem)}
                                        </Typography>
                                    </Box>
                                </Box>

                                {workflowCompletion.nextTask ? (
                                    <Box sx={{ pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                        <Typography component="span" sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6e6e73', display: 'block', mb: 0.75 }}>
                                            Next up
                                        </Typography>
                                        {/* Next task — small task card (mirrors LeadTaskList's
                                            TaskRow) with the EXISTING Assign / Reschedule / Pause
                                            actions surfaced inline via NextTaskActions. */}
                                        <NextTaskCard
                                            task={workflowCompletion.nextTask}
                                            action={(
                                                <NextTaskActions
                                                    task={workflowCompletion.nextTask}
                                                    pauseItemId={getPauseItemId(workflowCompletion.nextTask)}
                                                    practiceId={practiceId}
                                                    enqueueSnackbar={enqueueSnackbar}
                                                    onRescheduled={applyTaskReschedule}
                                                    onPaused={dropUpcomingTask}
                                                />
                                            )}
                                        />
                                        {/* Remaining upcoming tasks — same small task cards + the same
                                            inline Assign / Reschedule / Pause actions. Excludes the
                                            nextTask already shown above; only rendered post-completion. */}
                                        {(() => {
                                            const remaining = upcomingWorkflowTasks.filter(
                                                (task) => String(task?.id) !== String(workflowCompletion.nextTask?.id)
                                            );
                                            return remaining.length > 0 ? (
                                                <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                                                    {remaining.map((task) => (
                                                        <NextTaskCard
                                                            key={task.id}
                                                            task={task}
                                                            action={(
                                                                <NextTaskActions
                                                                    task={task}
                                                                    pauseItemId={getPauseItemId(task)}
                                                                    practiceId={practiceId}
                                                                    enqueueSnackbar={enqueueSnackbar}
                                                                    onRescheduled={applyTaskReschedule}
                                                                    onPaused={dropUpcomingTask}
                                                                />
                                                            )}
                                                        />
                                                    ))}
                                                </Stack>
                                            ) : null;
                                        })()}
                                    </Box>
                                ) : (
                                    <Box sx={{ pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Iconify icon="mdi:check-circle-outline" width={14} height={14} sx={{ color: '#34c759', flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#6e6e73', letterSpacing: '-0.005em' }}>
                                            All caught up
                                        </Typography>
                                    </Box>
                                )}

                                {/* Dismiss the Resolve panel and return to the inbox/thread. After a
                                    FOLLOW_UP / CLOSE / resolve outcome the conversation is already
                                    dispatched closed, so the panel must offer an explicit exit.
                                    onConfirmClose is the same panel-dismiss handler every call site
                                    wires up. Visually separated (own divider + spacing) and labelled by
                                    whether the conversation stayed open, so it reads as an exit, not an action
                                    on the next/upcoming task above it. */}
                                <Box sx={{ pt: 1.5, mt: 0.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                                    {/* Primary CTA: open the V2 task slideout for the next task so the rep
                                        can view / tick its checklist without leaving the context. Only shown
                                        when there IS a next task and the call site has wired up the handler
                                        (WaPopupChat → TaskDashboard / DashboardTaskListV2). */}
                                    {workflowCompletion.nextTask && onOpenWorkflow && (
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            disableElevation
                                            startIcon={<Iconify icon="mdi:clipboard-check-outline" width={16} height={16} />}
                                            onClick={() => onOpenWorkflow(workflowCompletion.nextTask)}
                                            sx={{ ...CRM_PRIMARY_BUTTON_SX, fontSize: '14px', fontWeight: 700, py: 0.9, mb: 1 }}
                                        >
                                            View task / checklist
                                        </Button>
                                    )}
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        disableElevation
                                        startIcon={<Iconify icon="eva:arrow-back-fill" width={16} height={16} />}
                                        onClick={() => onConfirmClose(workflowCompletion?.reason || 'Resolved')}
                                        sx={{ ...CRM_PRIMARY_BUTTON_SX, fontSize: '14px', fontWeight: 700, py: 0.9 }}
                                    >
                                        {workflowCompletion?.outcome === 'MESSAGE_SENT' ? 'Back to conversation' : 'Back to inbox'}
                                    </Button>
                                </Box>
                            </Stack>
                        </Box>
                    )}

                    {/* Archived / opted-out banner — shown when the lead is not in a state
                        that allows outreach. Kept outside the shared card so it floats
                        at the top of the body and is visible regardless of scroll position.
                        Archived: block everything (restore first). Opted-out: contact outcomes
                        blocked but admin close (OTHER) stays available. */}
                    {!workflowCompletion && (isArchived || isOptedOut) && (
                        <Box sx={{
                            mb: 2, p: 1.5, borderRadius: '12px',
                            bgcolor: isArchived ? 'rgba(255,86,48,0.06)' : 'rgba(255,171,0,0.07)',
                            border: `1px solid ${isArchived ? 'rgba(255,86,48,0.22)' : 'rgba(255,171,0,0.30)'}`,
                            display: 'flex', alignItems: 'flex-start', gap: 1,
                        }}>
                            <Iconify
                                icon={isArchived ? 'eva:alert-circle-fill' : 'eva:info-fill'}
                                width={18} height={18}
                                sx={{ color: isArchived ? '#FF5630' : '#B76E00', flexShrink: 0, mt: 0.125 }}
                            />
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: isArchived ? '#B71D18' : '#7A4100', lineHeight: 1.4 }}>
                                {/* eslint-disable-next-line no-nested-ternary */}
                                {isArchived
                                    ? (leadDetails?.is_spam
                                        ? 'This lead is flagged as spam — restore it first to take action.'
                                        : 'This lead is archived — restore the lead first to take action.')
                                    : 'This lead has opted out of communication — outreach outcomes are disabled.'}
                            </Typography>
                        </Box>
                    )}

                    {/* Initial / drill-in view = ONE cohesive card. The current-task section
                        (context + actions) and the "What happened?" picker / detail screens
                        share a single soft container — one light border + one calm shadow,
                        mirroring the guided-checklist surface — so they read as a continuous
                        unit (current task → what happened), not two floating shadowed boxes. */}
                    {!workflowCompletion && (
                    <Box sx={{
                        mb: 2,
                        borderRadius: '16px',
                        bgcolor: '#fff',
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                    }}>
                    {(workflowLoading || hasWorkflowTask) && (
                        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                            {workflowLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={14} sx={{ color: '#00AB55' }} />
                                    <Typography sx={{ fontSize: '13px', color: '#6e6e73' }}>Checking linked workflow...</Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.5}>
                                    {/* Eyebrow — dot + label in the action-type colour (mirrors UpNextCard) */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: workflowItemMeta.color, flexShrink: 0 }} />
                                        <Typography component="span" sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: workflowItemMeta.color }}>
                                            Current task
                                        </Typography>
                                    </Box>

                                    {/* Icon + title — hero presentation matching the guided checklist */}
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <Box sx={{
                                            width: 44, height: 44, borderRadius: '12px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            bgcolor: `${workflowItemMeta.color}1A`, border: `1.5px solid ${workflowItemMeta.color}33`, flexShrink: 0,
                                        }}>
                                            <Iconify icon={workflowItemMeta.icon} width={22} height={22} sx={{ color: workflowItemMeta.color }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '15px', color: '#1d1d1f', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
                                                {workflowLabel || getTaskLabel(workflowTask)}
                                                {taskTreatmentName && (
                                                    <Typography component="span" sx={{ fontSize: '13px', color: '#86868b', fontWeight: 500, ml: 0.75 }}>
                                                        {`· ${taskTreatmentName}`}
                                                    </Typography>
                                                )}
                                            </Typography>
                                            {getTaskDueLabel(workflowTask) && (
                                                <Typography sx={{ fontSize: '12px', color: '#86868b', fontWeight: 500, lineHeight: 1.3, mt: 0.25 }} noWrap>
                                                    {getTaskDueLabel(workflowTask)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Current task gets the SAME action cluster as the next/upcoming
                                        cards — NextTaskActions bundles the assignee row (avatars +
                                        assign picker the user likes) + Reschedule + Pause, all reusing
                                        the existing components/handlers. Reschedule patches this task's
                                        due_at in place; Pause re-picks the current item (handleCurrentTaskPaused).
                                        When the lead is archived, the entire cluster is disabled (pointer-events
                                        off + reduced opacity) — the rep cannot take actions on archived leads. */}
                                    {isRealWorkflowTaskId(workflowTask?.id) && (
                                        <Tooltip
                                            title={isArchived ? 'Lead is archived — restore first' : ''}
                                            placement="top"
                                            disableHoverListener={!isArchived}
                                            disableFocusListener={!isArchived}
                                            disableTouchListener={!isArchived}
                                        >
                                            <Box sx={isArchived ? { opacity: 0.4, pointerEvents: 'none', userSelect: 'none' } : {}}>
                                                <NextTaskActions
                                                    task={workflowTask}
                                                    pauseItemId={getChecklistItemId(workflowItem) || getPauseItemId(workflowTask)}
                                                    practiceId={practiceId}
                                                    enqueueSnackbar={enqueueSnackbar}
                                                    onRescheduled={applyTaskReschedule}
                                                    onPaused={handleCurrentTaskPaused}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )}

                                    {/* The current-task card is CONTEXT — task name/due + assignees +
                                        actions. Outcome selection lives in the "What happened?"
                                        picker directly below (same card). The redundant "choose
                                        below…" hint was removed — the heading already says it. */}
                                </Stack>
                            )}
                        </Box>
                    )}
                    {/* Lower section of the shared card — the picker / detail screens. Padded
                        to match the current-task section; no own shadow so it flows continuously. */}
                    <Box sx={{ p: 2 }}>

                    {/* Empty-state hint (spec #3) — shown above the picker when there is no
                        linked lead or no open task. Kept subtle (muted text + soft border) so it
                        doesn't compete with the archived/opted-out amber banner. The picker stays
                        rendered in both states so the rep can still resolve the conversation. */}
                    {!selectedOutcome && !workflowCompletion && emptyState && (
                        <Box sx={{
                            mb: 1.5, px: 1.5, py: 1.25, borderRadius: '10px',
                            bgcolor: 'rgba(145,158,171,0.06)',
                            border: '1px solid rgba(145,158,171,0.16)',
                            display: 'flex', alignItems: 'flex-start', gap: 1,
                        }}>
                            <Iconify
                                icon="eva:info-outline"
                                width={16} height={16}
                                sx={{ color: '#919EAB', flexShrink: 0, mt: 0.15 }}
                            />
                            <Box>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#637381', lineHeight: 1.3 }}>
                                    {emptyState.title}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#919EAB', fontWeight: 400, lineHeight: 1.3, mt: 0.25 }}>
                                    {emptyState.subtitle}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Outcome selection — two axes: the contact happened (checklist item
                        completes) and the conversation state (stays open / resolves / books /
                        follows up / closes). "Sent" splits into keep-open vs resolved so the rep
                        says explicitly what happens to the thread.
                        Options are disabled (not removed) when the lead is archived or opted-out,
                        so the layout is stable and the rep can read what they're missing. */}
                    {!selectedOutcome && (
                    <>
                    <Typography sx={{ fontSize: '12px', color: '#919EAB', mb: 2, fontWeight: 500 }}>What happened?</Typography>
                    {/* Opted-out inline note removed (M-1): the top-of-body amber banner already
                        explains the state; a second note below the heading was duplicate signal.
                        Per-row tooltips on each blocked option provide the final explanation. */}
                    <Stack spacing={0.75}>
                        {[
                            { key: 'MESSAGE_SENT', icon: 'ri:whatsapp-fill', label: 'Sent — awaiting reply', sub: 'Conversation stays open', color: '#00AB55', bg: 'rgba(0,171,85,0.06)' },
                            { key: 'SENT_RESOLVED', icon: 'eva:checkmark-circle-2-fill', label: 'Sent — resolved', sub: 'Sent and close the conversation', color: '#00AB55', bg: 'rgba(0,171,85,0.06)' },
                            { key: 'APPOINTMENT_BOOKED', icon: 'eva:calendar-fill', label: 'Booked', sub: 'Book an appointment', color: '#00AB55', bg: 'rgba(0,171,85,0.06)' },
                            // "Follow-up needed" is the manual way to schedule the next touch.
                            // When a checklist item exists, completing it auto-surfaces the next
                            // contact item (+ its Reschedule), so this manual option is redundant
                            // and is hidden in Mode A — it only shows when there's no checklist item.
                            ...(hasWorkflowItem ? [] : [
                                { key: 'FOLLOW_UP', icon: 'eva:clock-fill', label: 'Follow-up needed', sub: 'Set a reminder for next time', color: '#078DEE', bg: 'rgba(7,141,238,0.06)' },
                            ]),
                            { key: 'OTHER', icon: 'eva:close-circle-fill', label: 'Close / not interested', sub: 'Archive, flag or close', color: '#637381', bg: 'rgba(145,158,171,0.06)' },
                        ].map((opt) => {
                            // Distinct selected styling so the click obviously registers —
                            // filled tint + accent ring + a trailing check, mirroring the
                            // guided-checklist active-row language (colour1A bg / colour33 ring).
                            const isSelected = selectedOutcome === opt.key;
                            // Guard: blocked by archived (all outcomes) or opted-out (contact outcomes only).
                            const isBlocked = !isOutcomeAllowed(opt.key, { isArchived, isOptedOut });
                            // eslint-disable-next-line no-nested-ternary
                            const blockedTip = isArchived
                                ? 'Lead is archived — restore first'
                                : 'Lead has opted out — outreach is disabled';
                            return (
                            <Tooltip key={opt.key} title={isBlocked ? blockedTip : ''} placement="left" disableHoverListener={!isBlocked} disableFocusListener={!isBlocked} disableTouchListener={!isBlocked}>
                            <Box
                                role="button"
                                aria-pressed={isSelected}
                                aria-disabled={isBlocked}
                                tabIndex={isBlocked ? -1 : 0}
                                onClick={isBlocked ? undefined : () => { setSelectedOutcome(opt.key); setSaveResult(null); }}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    px: 1.5, py: 1.25, borderRadius: '12px',
                                    transition: 'all 200ms ease',
                                    ...(isBlocked ? {
                                        cursor: 'not-allowed',
                                        opacity: 0.45,
                                        background: 'transparent',
                                        border: '1.5px solid transparent',
                                        pointerEvents: 'auto', // keep hover for Tooltip
                                    } : {
                                        cursor: 'pointer',
                                        background: isSelected ? opt.bg : 'transparent',
                                        border: isSelected ? `1.5px solid ${opt.color}` : '1.5px solid transparent',
                                        '&:hover': { background: opt.bg },
                                        '&:active': { transform: 'scale(0.98)' },
                                    }),
                                }}>
                                <Box sx={{
                                    width: 36, height: 36, borderRadius: '10px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isSelected ? opt.color : opt.bg, flexShrink: 0,
                                }}>
                                    <Icon icon={opt.icon} width={18} style={{ color: isSelected ? '#fff' : opt.color }} />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#212B36', lineHeight: 1.3 }}>{opt.label}</Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#919EAB', fontWeight: 400, lineHeight: 1.3 }}>{opt.sub}</Typography>
                                </Box>
                                {isSelected
                                    ? <Icon icon="eva:checkmark-circle-2-fill" width={18} style={{ color: opt.color, flexShrink: 0 }} />
                                    : <Icon icon="eva:chevron-right-fill" width={16} style={{ color: '#C4CDD5', flexShrink: 0 }} />}
                            </Box>
                            </Tooltip>
                            );
                        })}
                    </Stack>
                    </>
                    )}

                    {/* Defensive fallback only: MESSAGE_SENT normally lands on the
                        completion card. If no workflowCompletion was produced because
                        there was no task context, keep the old light confirmation. */}
                    {selectedOutcome === 'MESSAGE_SENT' && saveResult === 'success' && !workflowCompletion && (
                        <Box sx={{
                            p: 1.75,
                            borderRadius: RADIUS,
                            bgcolor: 'rgba(0,171,85,0.06)',
                            border: '1px solid rgba(0,171,85,0.20)',
                            display: 'flex', alignItems: 'flex-start', gap: 1,
                        }}>
                            <Iconify icon="mdi:check-circle" width={20} height={20} sx={{ color: '#00AB55', flexShrink: 0, mt: 0.125 }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#212B36', lineHeight: 1.3 }}>
                                    Sent — awaiting reply
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#637381', lineHeight: 1.35, mt: 0.25 }}>
                                    Conversation kept open · returning you to the thread…
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Message sent screen */}
                    {selectedOutcome === 'MESSAGE_SENT' && saveResult !== 'success' && (
                        <Stack spacing={1.5}>
                            <Box sx={{
                                p: 1.25,
                                borderRadius: RADIUS,
                                bgcolor: 'rgba(0,171,85,0.04)',
                                border: '1px solid rgba(0,171,85,0.12)',
                            }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#212B36', lineHeight: 1.25 }}>
                                    Complete {workflowLabel || 'this checklist item'}
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#637381', lineHeight: 1.35, mt: 0.35 }}>
                                    Use this when the WhatsApp message has been sent and the conversation may still continue. The conversation will stay open.
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Note</Typography>
                                <TextField size="small" fullWidth multiline minRows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="e.g., Sent first contact message, waiting for reply"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1px' } } }} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Icon icon="mdi:information-outline" width={12} style={{ color: '#919EAB', marginTop: 2 }} />
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', lineHeight: 1.35 }}>
                                    Will add this WhatsApp note, mark {workflowLabel || 'the checklist item'} complete, and show the next task. It will not close the conversation.
                                </Typography>
                            </Box>
                        </Stack>
                    )}

                    {/* Sent — resolved screen */}
                    {selectedOutcome === 'SENT_RESOLVED' && (
                        <Stack spacing={1.5}>
                            <Box sx={{
                                p: 1.25,
                                borderRadius: RADIUS,
                                bgcolor: 'rgba(0,171,85,0.04)',
                                border: '1px solid rgba(0,171,85,0.12)',
                            }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#212B36', lineHeight: 1.25 }}>
                                    Complete {workflowLabel || 'this checklist item'} & resolve
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#637381', lineHeight: 1.35, mt: 0.35 }}>
                                    Use this when the WhatsApp message has been sent and nothing more is needed. The conversation will be closed and marked resolved.
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Note</Typography>
                                <TextField size="small" fullWidth multiline minRows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="e.g., Answered their question, no further action needed"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1px' } } }} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Icon icon="mdi:information-outline" width={12} style={{ color: '#919EAB', marginTop: 2 }} />
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', lineHeight: 1.35 }}>
                                    Will add this WhatsApp note, mark {workflowLabel || 'the checklist item'} complete, and close the conversation as resolved.
                                </Typography>
                            </Box>
                        </Stack>
                    )}

                    {/* Follow Up screen */}
                    {selectedOutcome === 'FOLLOW_UP' && (
                        <Stack spacing={1.5}>
                            {/* Did you also call? */}
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Contact method</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Box onClick={() => setCalledConfirmed(true)} sx={{ ...compactChipStyle(calledConfirmed === true), fontSize: '11px', px: 1, py: 0.375 }}>
                                        <Icon icon="eva:phone-fill" width={14} style={{ marginRight: 4 }} /> Yes
                                    </Box>
                                    <Box onClick={() => setCalledConfirmed(false)} sx={{
                                        ...compactChipStyle(calledConfirmed === false), fontSize: '11px', px: 1, py: 0.375,
                                        ...(calledConfirmed === false && { borderColor: '#637381', color: '#fff', background: '#637381' }),
                                    }}>
                                        <Icon icon="ri:whatsapp-fill" width={14} style={{ marginRight: 4 }} /> WA only
                                    </Box>
                                </Box>
                            </Box>

                            {/* Call outcome — only when "Yes" */}
                            {calledConfirmed === true && (
                                <Box>
                                    <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Leave a note about the call attempt</Typography>
                                    <TextField size="small" fullWidth multiline minRows={2} value={callNote} onChange={(e) => setCallNote(e.target.value)}
                                        placeholder="Enter or select details about the call attempt"
                                        sx={{ mb: 0.75, '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' } } }} />
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {['No answer', 'Left voicemail', 'Incorrect number', 'Call declined', 'Poor call quality', 'Reached different person', 'Busy line', 'Requested call-back'].map((opt) => (
                                            <Box key={opt} onClick={() => {
                                                const newSelected = selectedCallOptions.includes(opt) ? selectedCallOptions.filter(v => v !== opt) : [...selectedCallOptions, opt];
                                                setSelectedCallOptions(newSelected);
                                                setCallNote(newSelected.join(', '));
                                            }}
                                                sx={{ ...compactChipStyle(selectedCallOptions.includes(opt)), fontSize: '11px', px: 1, py: 0.375 }}>{opt}</Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {/* Reminder presets */}
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Remind me</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {REMINDER_PRESETS.map((preset) => (
                                        <Box key={preset.key} onClick={() => setReminderPreset(preset.key)}
                                            sx={{
                                                ...compactChipStyle(reminderPreset === preset.key), fontSize: '11px', px: 1, py: 0.375,
                                                ...(reminderPreset === preset.key && preset.key === 'none' && { borderColor: '#637381', color: '#fff', background: '#637381' }),
                                            }}>
                                            {preset.label}
                                        </Box>
                                    ))}
                                </Box>
                                {reminderPreset === 'custom' && (
                                    <DateTimePicker
                                        value={customDate}
                                        onChange={(val) => setCustomDate(val)}
                                        disablePast
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'Pick date & time',
                                                sx: {
                                                    mt: 0.75,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: RADIUS, fontSize: '12px',
                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1px' },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Assignees */}
                            {reminderPreset && reminderPreset !== 'none' && (
                                <AssignedToLead label="Assignees" assignToState={assignToState} setAssignTo={setAssignToState} />
                            )}

                            {/* Notes */}
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Notes</Typography>
                                <TextField size="small" fullWidth multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="e.g., Left voicemail, will call back next week"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1px' } } }} />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Icon icon="mdi:information-outline" width={12} style={{ color: '#919EAB' }} />
                                {/* eslint-disable no-nested-ternary */}
                                <Typography sx={{ fontSize: '10px', color: '#919EAB' }}>
                                    {hasWorkflowItem
                                        ? `Will add a WhatsApp note, close this conversation, complete ${workflowLabel}, and show the next task`
                                        : 'Will add a WhatsApp note and close this conversation'}
                                    {reminderPreset && reminderPreset !== 'none' && ` · Reminder: ${reminderPreset === 'custom' ? (customDate ? moment(customDate).format('DD MMM, HH:mm') : 'Custom') : REMINDER_PRESETS.find(p => p.key === reminderPreset)?.label}`}
                                </Typography>
                                {/* eslint-enable no-nested-ternary */}
                            </Box>
                        </Stack>
                    )}

                    {/* Close screen */}
                    {selectedOutcome === 'OTHER' && (
                        <Stack spacing={1.5}>
                            {/* Contact method */}
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Contact method</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Box onClick={() => setCalledConfirmed(true)} sx={{ ...compactChipStyle(calledConfirmed === true), fontSize: '11px', px: 1, py: 0.375 }}>
                                        <Icon icon="eva:phone-fill" width={14} style={{ marginRight: 4 }} /> Yes
                                    </Box>
                                    <Box onClick={() => setCalledConfirmed(false)} sx={{
                                        ...compactChipStyle(calledConfirmed === false), fontSize: '11px', px: 1, py: 0.375,
                                        ...(calledConfirmed === false && { borderColor: '#637381', color: '#fff', background: '#637381' }),
                                    }}>
                                        <Icon icon="ri:whatsapp-fill" width={14} style={{ marginRight: 4 }} /> WA only
                                    </Box>
                                </Box>
                            </Box>

                            {/* Call outcome — only when "Yes" */}
                            {calledConfirmed === true && (
                                <Box>
                                    <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Leave a note about the call attempt</Typography>
                                    <TextField size="small" fullWidth multiline minRows={2} value={callNote} onChange={(e) => setCallNote(e.target.value)}
                                        placeholder="Enter or select details about the call attempt"
                                        sx={{ mb: 0.75, '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' } } }} />
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {['No answer', 'Left voicemail', 'Incorrect number', 'Call declined', 'Poor call quality', 'Reached different person', 'Busy line', 'Requested call-back'].map((opt) => (
                                            <Box key={opt} onClick={() => {
                                                const newSelected = selectedCallOptions.includes(opt) ? selectedCallOptions.filter(v => v !== opt) : [...selectedCallOptions, opt];
                                                setSelectedCallOptions(newSelected);
                                                setCallNote(newSelected.join(', '));
                                            }}
                                                sx={{ ...compactChipStyle(selectedCallOptions.includes(opt)), fontSize: '11px', px: 1, py: 0.375 }}>{opt}</Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Archive</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {['Not Interested', 'Too Expensive', 'Gone Elsewhere', 'Duplicate'].map((r) => (
                                        <Box key={r} onClick={() => setCloseReason(r)}
                                            sx={{ ...compactChipStyle(closeReason === r), fontSize: '11px', px: 1, py: 0.375 }}>
                                            {r}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Flag</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {['Spam'].map((r) => (
                                        <Box key={r} onClick={() => setCloseReason(r)}
                                            sx={{ ...compactChipStyle(closeReason === r), fontSize: '11px', px: 1, py: 0.375 }}>
                                            {r}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Just close</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {['General Chat', 'Resolved'].map((r) => (
                                        <Box key={r} onClick={() => setCloseReason(r)}
                                            sx={{ ...compactChipStyle(closeReason === r), fontSize: '11px', px: 1, py: 0.375 }}>
                                            {r}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Notes</Typography>
                                <TextField size="small" fullWidth multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Additional details..."
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: RADIUS, fontSize: '12px', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00AB55', borderWidth: '1px' } } }} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Icon icon="mdi:information-outline" width={12} style={{ color: '#919EAB', marginTop: 2 }} />
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', lineHeight: 1.35 }}>
                                    {hasWorkflowItem
                                        ? `Will add this note, close the conversation, complete ${workflowLabel}, and show the next task.`
                                        : 'Will add this note and close the conversation.'}
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                    </Box>
                    </Box>
                    )}
                </Box>

                {/* Footer — the single primary confirm CTA for the active outcome.
                    Hidden on success (getFooterButton returns null) so it never echoes a
                    second green button next to the completion card / light banner. */}
                {footerButton && (
                    <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid rgba(145,158,171,0.12)', flexShrink: 0 }}>
                        {/* eslint-disable no-nested-ternary */}
                        <Button variant="contained" fullWidth onClick={footerButton.onClick}
                            disabled={footerButton.disabled}
                            startIcon={footerButton.icon ? (isSaving ? <CircularProgress size={14} color="inherit" /> : <Icon icon={footerButton.icon} width={16} />) : <CircularProgress size={14} color="inherit" />}
                            sx={{
                                background: footerButton.color,
                                '&:hover': { background: footerButton.color === '#FF5630' ? '#B71D18' : '#007B55' },
                                '&.Mui-disabled': { background: '#f4f6f8', color: '#C4CDD5' },
                                textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: RADIUS, py: 1, boxShadow: 'none',
                            }}>
                            {footerButton.label}
                        </Button>
                        {/* eslint-enable no-nested-ternary */}
                    </Box>
                )}

                {/* Confirmation dialog — shown before archive / spam actions only.
                    Resolved / General Chat proceed without this dialog (one-click). */}
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    title={FLAG_REASONS.includes(closeReason) ? 'Mark as spam?' : 'Archive this lead?'}
                    content={
                        FLAG_REASONS.includes(closeReason)
                            ? 'This will flag the lead as spam and move it to the Archived tab.'
                            : `This will archive the lead (${closeReason}) and move it to the Archived tab.`
                    }
                    action={
                        <Button
                            variant="contained"
                            color={FLAG_REASONS.includes(closeReason) ? 'error' : 'warning'}
                            onClick={() => { setConfirmOpen(false); runClose(); }}
                        >
                            {FLAG_REASONS.includes(closeReason) ? 'Mark as spam' : 'Archive'}
                        </Button>
                    }
                />
            </Box>
        </Box>
    );
}

ResolvePanel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirmClose: PropTypes.func.isRequired,
    contactDetails: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onShareMessage: PropTypes.func,
    /** Closes the WA popup and opens the V2 task slideout for the given task object. */
    onOpenWorkflow: PropTypes.func,
    /** Test-only: pre-seeds workflowCompletion state to render the completion card without a live API round-trip. */
    _testWorkflowCompletion: PropTypes.object,
};
